<template>
  <div>
    <v-select
      :value="null"
      :options="availableOptions"
      :reduce="option => option.value"
      :loading="loading"
      :disabled="disabled"
      label="label"
      placeholder="Search for an area, region, mission block, RMC, district or country"
      @input="addPlace"
    >
      <template #option="option">
        <div class="d-flex justify-content-between align-items-center">
          <span>{{ option.label }}</span>
          <small class="text-muted text-nowrap ml-1">{{ option.levelLabel }}</small>
        </div>
      </template>

      <template #no-options>
        <span class="small text-muted">
          {{ loading ? 'Loading places…' : 'No place matches that name.' }}
        </span>
      </template>
    </v-select>

    <div v-if="!value.length" class="text-muted small mt-50">
      No place chosen yet — this client would reach no records at all.
    </div>

    <div v-else class="mt-1">
      <b-badge
        v-for="place in chosenPlaces"
        :key="place.token"
        variant="light-primary"
        class="mr-50 mb-50 py-50 px-1"
      >
        <span class="font-weight-bold">{{ place.label }}</span>
        <span class="text-muted small ml-50">{{ place.levelLabel }}</span>
        <feather-icon
          v-if="!disabled"
          icon="XIcon"
          size="14"
          class="ml-50 cursor-pointer"
          @click="removePlace(place.token)"
        />
      </b-badge>

      <div class="text-muted small mt-25">
        Everything under {{ value.length === 1 ? 'this place' : 'these places' }} is
        included automatically — districts, assemblies, countries and their members.
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash'
import vSelect from 'vue-select'
import { BBadge } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import unwrapList from './unwrapList'
import locationLabel from './locationLabel'

/**
 * One search box over every level of both hierarchies, rather than the advanced
 * picker's choose-a-level / choose-a-value / press-Add sequence. An admin types
 * "Ashanti", sees "Ashanti — Area (Domestic)", and picks it; the level is something
 * they read off the result rather than something they have to know up front.
 *
 * Assemblies are deliberately absent. They number in the thousands, so listing them
 * whole would be slow and would silently truncate, and an assembly-level grant is not
 * the case this view exists for — the advanced picker still offers them.
 */
/**
 * `paged: false` marks the endpoints that return their collection whole. Their routes
 * are `strict`, so sending a `limit` they do not declare is rejected outright with
 * "Unknown fields received limit" rather than ignored.
 */
const LEVELS = [
  { field: 'area_id', label: 'Area', hierarchy: 'Domestic', service: 'fetchGeoAreas', paged: true },
  { field: 'region_id', label: 'Region', hierarchy: 'Domestic', service: 'fetchGeoRegions', paged: false },
  { field: 'district_id', label: 'District', hierarchy: 'Domestic', service: 'fetchGeoDistricts', paged: true },
  { field: 'mission_block_id', label: 'Mission Block', hierarchy: 'Mission', service: 'fetchMissionBlocks', paged: true },
  { field: 'rmc_id', label: 'RMC', hierarchy: 'Mission', service: 'fetchRmc', paged: true },
  { field: 'country_id', label: 'Country', hierarchy: 'Mission', service: 'fetchCountries', paged: false },
]

export default {
  components: {
    BBadge,
    vSelect,
  },
  props: {
    // array of "<field>:<id>" tokens, the same vocabulary the API parses
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      options: [],
    }
  },
  computed: {
    /** Already-chosen places drop out of the list so they cannot be added twice. */
    availableOptions() {
      return this.options.filter(option => !this.value.includes(option.value))
    },
    chosenPlaces() {
      return this.value.map(token => {
        const match = this.options.find(option => option.value === String(token))
        if (match) return { ...match, token }

        // Options still loading, or a grant at a level this picker does not list
        // (an assembly, say) — show the raw token rather than an empty chip.
        const separator = String(token).indexOf(':')
        const field = separator === -1 ? 'area_id' : String(token).slice(0, separator)
        const level = LEVELS.find(entry => entry.field === field)

        return {
          token,
          label: separator === -1 ? String(token) : String(token).slice(separator + 1),
          levelLabel: level ? level.label : field,
        }
      })
    },
  },
  created() {
    this.loadPlaces()
  },
  methods: {
    addPlace(token) {
      if (!token || this.value.includes(token)) return
      this.$emit('input', [...this.value, token])
    },
    removePlace(token) {
      this.$emit('input', this.value.filter(entry => entry !== token))
    },
    async loadPlaces() {
      try {
        this.loading = true

        // Levels load in parallel and are flattened into one list; the level is shown
        // per row rather than being a separate control.
        const results = await Promise.all(LEVELS.map(level => this.fetchLevel(level)))

        this.options = results.flat()
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
    async fetchLevel(level) {
      const service = this.useJwt().sharedService

      // These are reference collections in the tens to low hundreds; an admin needs to
      // search all of them, not page one. The un-paged endpoints already return the
      // whole collection, and reject a `limit` they never declared.
      const params = level.paged ? { limit: 1000 } : {}
      const rows = unwrapList(await service[level.service](params))

      return rows.map(row => ({
        value: `${level.field}:${row._id}`,
        label: locationLabel(row),
        levelLabel: `${level.label} · ${level.hierarchy}`,
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
